/**
 * Split the WIP list's Id space into ranges one page each can hold.
 *
 * SharePoint's pager follows `__next`, so a 34,000-row list is seven requests
 * that cannot overlap. `Id` is indexed on every list, so `Id gt lo and Id le
 * hi` is allowed past the 5,000-item view threshold, and a range no wider than
 * a page can never return more than a page — which lets the ranges be read AT
 * ONCE. Ids are unique, so the width is a hard ceiling on the rows a range
 * returns however many Ids have been deleted from it.
 *
 * Pure.
 */
export function idRanges(maxId: number, width: number): { lo: number; hi: number }[] {
  const out: { lo: number; hi: number }[] = [];
  if (!(maxId > 0) || !(width > 0)) return out;
  for (let lo = 0; lo < maxId; lo += width) out.push({ lo, hi: Math.min(lo + width, maxId) });
  return out;
}

/** Run `fn` over `items` with at most `limit` in flight, results in order. */
export async function mapPool<T, R>(items: readonly T[], limit: number, fn: (t: T) => Promise<R>): Promise<R[]> {
  const out = new Array<R>(items.length);
  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < items.length) {
      const i = next++;
      out[i] = await fn(items[i]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
}
